import * as React from "react";

import "./pricing-table-modal.scss";

import {enableCanadaMailing}            from "@Common/logic/enable-canada-mailing";
import {
    getPickPackOptions, is3rdPartyWorkflow, isPickPack, isKit
}                                       from "@Common/logic/template-options/template-options-pickpack-data-logic";
import {cs}                             from "@Common/react/chain-services";
import {consumeContext}                 from "@Common/react/context";
import {keyed, keyed1}                  from "@Common/react/keyed";
import {UseState}                       from "@Common/react/use-state";
import {DeprecatedModalService2}                   from "@Common/ui-components/heavy/common/design2/modal-service/modal-service";
import {flatten1, sortMulti}            from "@Common/utils/collections";
import {unique}                         from "@Common/utils/collections";
import {chain}                          from "@Common/utils/fs";
import {getEddmLineItemPrice} from "@Common/logic/template-options/template-options-eddm-line-item-price";

import {getDefaultPaperType} from "../logic/get-default-paper-type";

import {ExpandAllToggle}   from "./expand-all-toggle/expand-all-toggle";
import {PaperPricingTable} from "./paper-pricing-table/paper-pricing-table";
import {Tabs}              from "./tabs/tabs";

export const PricingTableModal = ({templateOptions, next: rootNext}) => cs(
    ({}, next) => DeprecatedModalService2({
        render: () => ({
            size: "xsmall",
            content: next(),
        }),
        next: rootNext,
    }),


    ({}, next) => (
        <div className="pricing-table-modal-81h">
            <div className="title">Pricing</div>
            {next()}
        </div>
    ),
    ({}, next) => !templateOptions?.distributions?.length ? (
        <div className="no-data">
            This template does not currently have delivery options configured.
            Please choose another template or contact customer service.
        </div>
    ) : (
        next()
    ),
    ["deliveryTypes", ({}, next) => next(
        flatten1(
            templateOptions?.distributions.map((distribution) => (
                distribution !== "mailing" ? (
                    [{type: distribution}]
                ) : (
                    Object.keys(templateOptions.mailingClasses).map((class1) => ({
                        subType: class1,
                        type: "mailing",
                    }))
                )
            ))
        )
    )],
    ["canadaMailing", (_, next) => enableCanadaMailing({templateOptions, next})],
    ["isNDM", (_, next) => next(templateOptions?.isNDM)],
    ["deliveryTabs", ({deliveryTypes, canadaMailing}, next) => Tabs({
        tabs: (() => {
            if (deliveryTypes.some((dt2) => dt2.subType == "5")) {
                return [deliveryTypes.find((dt) => dt.subType == "5")];
            } else {
                return deliveryTypes;
            }
        })(),
        getLabel: ({type, subType}) => (
            type !== "mailing" ? (
                type.toUpperCase()
            ) : (
                getMailingClassLabels(canadaMailing, deliveryTypes.some((dt) => dt.subType == "5"))[subType]
            )
        ),
        initValue: (
            deliveryTypes.find((dt) => dt.subType == "5") ?? 
            deliveryTypes.find((dt) => dt.type === templateOptions?.defaultDeliveryMethod) ??
            deliveryTypes[0]
        ),
        next,
    })],

    ({deliveryTabs}, next) => <>
        {deliveryTabs.renderHeader()}

        {next()}
    </>,


    ({deliveryTabs}, next) => keyed1({key: deliveryTabs.currentTab, next}),
    ["deliveryPaperTypes", ({deliveryTabs}, next) => next(getDeliveryPaperTypes(
        templateOptions?.options.filter((option) => option.group === "PaperTypes"),
        deliveryTabs.currentTab,
    ))],
    ["expandedPaperTypes", ({deliveryPaperTypes}, next) => UseState({
        initValue: [getDefaultPaperType(deliveryPaperTypes)?.name],
        next,
    })],
    consumeContext("session"),
    consumeContext("company"),
    ({
         expandedPaperTypes, deliveryPaperTypes, deliveryTabs,
         session, company, canadaMailing, isNDM,
    }) => deliveryPaperTypes.length === 0 ? (
        (isPickPack(templateOptions) || is3rdPartyWorkflow(templateOptions)) ? (
            <div className="title" xd-qa-type="text" xd-qa-name="No Pricing Error">Pricing has not been configured for this product</div>
        ) : (
            <div className="title" xd-qa-type="text" xd-qa-name="No Paper Type Error">No paper type has been set for this delivery option</div>
        )
    ) : <>
        {
            !isKit(templateOptions) &&
            !isPickPack(templateOptions) &&
            !is3rdPartyWorkflow(templateOptions) &&
            deliveryTabs.currentTab.type !== "download" && (
                deliveryPaperTypes.length === 1 ? (
                    <div className="title paper-type">
                        Paper Type
                    </div>
                ) : (
                    ExpandAllToggle({
                        onClick: () => {
                            expandedPaperTypes.onChange(
                                deliveryPaperTypes.map((pt) => pt.name)
                            );
                        },
                    })
                )
            )
        }

        <div className="paper-types">
            {deliveryPaperTypes.map((paperType, i) => cs(
                keyed(i),
                () => PaperPricingTable({
                    deliveryPaperTypes,
                    paperType,
                    focusedDelivery: deliveryTabs.currentTab,
                    expanded: {
                        value: expandedPaperTypes.value.includes(paperType.name),
                        onChange: (expanded) => expandedPaperTypes.change((_) => (
                            !expanded ? (
                                _.filter((p) => p !== paperType.name)
                            ) : (
                                [..._, paperType.name]
                            )
                        )),
                    },
                    hidePaperTypes: (
                        is3rdPartyWorkflow(templateOptions) || 
                        isPickPack(templateOptions) || 
                        isKit(templateOptions)
                    ),
                    defaultViewAllPrices: deliveryPaperTypes.length === 1,
                    isNDM,
                    hasEddm: (
                        (!session.data.branch || session.data.branch.addressListTypes?.includes("saturation")) &&
                        company?.prefs?.addressListTypes?.includes("saturation") &&
                        (!canadaMailing)
                    ),
                    eddmPrice: getEddmLineItemPrice(templateOptions)
                })
            ))}
        </div>
    </>,
);


const getDeliveryPaperTypes = (paperTypes, deliveryMethod) => chain(
    paperTypes,
    (_) => _.filter((p) => p.distributionFilter.includes(deliveryMethod.type)),
    (_) => unique(_, (pt) => pt.name),
    (_) => sortMulti(_, [(paperType) => paperType.name.toLowerCase()]),
);

const getMailingClassLabels = (canadaMailing, ndmServiceLevel) => {
    if (ndmServiceLevel) {
        return {
            "5": "NEIGHBORHOOD DIRECT MAIL",
        }
    }
    if (canadaMailing) {
        return {
            "1": "CANADIAN LETTERMAIL",
            "3": "CANADIAN ADMAIL",
        }
    }
    return {
        "1": "FIRST CLASS MAIL",
        "3": "STANDARD MAIL",
    }
};
